import { BaseViewStore, HyperlinkCellData, ImageCellData } from 'core/stores';
import { ExitScanModeForm } from 'modules/order/forms';
import { PalletStore, FulfillmentOrderStore } from 'modules/order/stores';
import { ApplicationStore } from 'platform/application/stores';
import { formInvalidate, isSeller, isSuperAdminOrganization } from 'core/utils';
import { SoundService } from 'modules/sound/services';
import { action, observable, makeObservable } from 'mobx';
import { sourceOfChange } from 'common/constants';
import _ from 'lodash';

class ExitScanModeViewStore extends BaseViewStore {
	@observable exitScanNotification = null;
	@observable selectedPallet = null;
	@observable selectedWarehouse = null;
	@observable fulfillmentOrders = [];
	@observable palletOptions = [];
	@observable warehouseOptions = [];
	@observable isWarehouseDropdownLoading = false;
	@observable isPalletDropdownLoading = false;
	@observable page = 1;
	@observable infiniteSearchPhrase = null;
	selectReference = null;
	debouncedWarehouseSearch = null;
	warehouseReference = null;
	debouncedSearch = null;

	form = new ExitScanModeForm();

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.parentView = this.rootStore.orderListViewStore;
		this.parentView.isPickingMode = false;
		this.soundService = new SoundService();
		this.parentView.reload = true;
		this.palletStore = new PalletStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.organizationStore = new ApplicationStore(rootStore);
		this.isSeller = isSeller(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.defineTableData();
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (!this.isSeller && !this.isSuperAdminOrganization) {
			await this.findPallet();
		}
		if (this.isSeller || this.isSuperAdminOrganization) {
			await this.findWarehouses();
		}
		this.debouncedWarehouseSearch = _.debounce((searchPhrase, { action }) => {
			this.searchWarehouseAsync(searchPhrase, { action });
		}, 500);
		this.debouncedSearch = _.debounce((searchPhrase, { action }) => {
			this.searchPalletAsync(searchPhrase, { action });
		}, 500);
		this.resume();
	};

	@action.bound async findPallet() {
		await this.palletStore.findPallet({
			rpp: 50,
			page: 1,
			isOpened: true,
			isSeller: this.isSeller || this.isSuperAdminOrganization,
			embed: [
				'shipmentInfos',
				'shipmentInfos.fulfillmentOrder',
				'shipmentInfos.fulfillmentOrder.fulfillmentOrderStatus',
				'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems',
				'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems.product',
				'carrierService',
				'carrierService.carrierServiceType',
			],
			warehouseIds:
				(this.isSeller || this.isSuperAdminOrganization) && this.selectedWarehouse
					? [this.selectedWarehouse.value]
					: undefined,
		});
		this.palletOptions = _.map(this.palletStore.data, item => {
			return { value: item.id, label: item.barcode, extra: item };
		});
	}

	@action.bound
	findWarehouses = async () => {
		await this.organizationStore.find({ page: this.page, rpp: 15, warehouseOnly: true, isOrganizationActive: true });
		this.warehouseOptions = _.map(this.organizationStore.data, item => {
			return { value: item.id, label: item.applicationTitle };
		});
	};

	@action.bound
	handleOnMenuScroll = async () => {
		if (this.warehouseOptions.length < this.page * 15) return;
		this.isWarehouseDropdownLoading = true;
		this.page++;
		await this.organizationStore.find({
			search: this.infiniteSearchPhrase,
			page: this.page,
			rpp: 15,
			warehouseOnly: true,
			isOrganizationActive: true,
		});
		if (this.organizationStore.response.item.length > 0) {
			const newOptions = _.map(this.organizationStore.data, item => {
				return { value: item.id, label: item.applicationTitle };
			});
			this.warehouseOptions = this.warehouseOptions.concat(newOptions);
		}
		this.isWarehouseDropdownLoading = false;
	};

	@action.bound
	onMenuClose = async () => {
		this.page = 1;
		this.infiniteSearchPhrase = null;
		await this.findWarehouses();
	};

	@action.bound
	searchWarehouseAsync = async (searchPhrase, { action }) => {
		if (searchPhrase === '') {
			this.page = 1;
		}
		if (action === 'input-change') {
			this.isWarehouseDropdownLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.page = 1;
			this.warehouseOptions = [];
			await this.organizationStore.find({
				search: searchPhrase || null,
				page: 1,
				rpp: 15,
				warehouseOnly: true,
				isOrganizationActive: true,
			});
			const newOptions = [];
			_.forEach(this.organizationStore.data, item => {
				newOptions.push({ value: item.id, label: item.applicationTitle, extra: item });
			});
			this.warehouseOptions = newOptions;
			this.isWarehouseDropdownLoading = false;
		}
	};

	@action.bound
	searchPalletAsync = async (searchPhrase, { action }) => {
		if (action === 'input-change') {
			this.isPalletDropdownLoading = true;
			this.palletOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 50,
				orderBy: 'barcode',
				orderDirection: 'asc',
				searchFields: 'barcode',
				isSeller: this.isSeller || this.isSuperAdminOrganization,
				embed: [
					'shipmentInfos',
					'shipmentInfos.fulfillmentOrder',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderStatus',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems.product',
					'carrierService',
					'carrierService.carrierServiceType',
				],
				isOpened: true,
				warehouseIds:
					(this.isSeller || this.isSuperAdminOrganization) && this.selectedWarehouse
						? [this.selectedWarehouse.value]
						: [],
			};
			await this.palletStore.findPallet(filter);
			var newOptions = [];
			if (this.palletStore.response.totalRecords == 1 && searchPhrase != '') {
				const pallet = this.palletStore.data[0];
				newOptions.push({ value: pallet.id, label: pallet.barcode, extra: pallet });
				this.onChange({ value: pallet.id, label: pallet.barcode, extra: pallet });
				this.selectReference.select.setState({ menuIsOpen: false, inputValue: '' });
			} else {
				if (this.palletStore.response.totalRecords == 0) {
					if (searchPhrase !== '') {
						this.exitScanNotification = 'FULFILLMENTORDER.EXITSCANMODE.NOTIFICATION.PALLETNOTFOUND';
						this.fulfillmentOrders = [];
						await this.soundService.error();
					}
					this.selectReference.select.setState({ menuIsOpen: false, inputValue: '' });
					_.forEach(this.palletOptions, item => {
						newOptions.push(item);
					});
				} else {
					_.forEach(this.palletStore.data, item => {
						newOptions.push({ value: item.id, label: item.barcode, extra: item });
					});
				}
			}
			this.palletOptions = newOptions;
			this.isPalletDropdownLoading = false;
		}
	};

	@action.bound
	exitScan = async () => {
		this.suspend();
		try {
			this.exitScanNotification = null;
			this.fulfillmentOrders = [];
			let { extra, value } = this.selectedPallet;
			await this.palletStore.exitScan({ id: value }, sourceOfChange.exitScanMode);
			this.transformData(extra);
			this.parentView.reload = true;
			this.exitScanNotification = 'FULFILLMENTORDER.EXITSCANMODE.NOTIFICATION.SUCCESS';
			setTimeout(() => {
				if (this.form.$('pallet')) {
					this.form.$('pallet').clear();
					this.selectedPallet = null;
				}
			}, 2000);
			await this.findPallet();
			await this.soundService.success();
		} catch (err) {
			if (err.statusCode === 400 && err.data && err.data.message) {
				this.exitScanNotification = err.data.message;
				await this.soundService.error();
			} else {
				formInvalidate({ form: this.form, err });
			}
		}
		this.resume();
	};

	onChange = async selectedOption => {
		this.fulfillmentOrders = [];
		if (selectedOption && selectedOption.value != null) {
			this.selectedPallet = selectedOption;
			this.exitScanNotification = null;
			await this.exitScan();
		} else {
			this.selectedPallet = null;
		}
	};

	@action.bound
	onWarehouseChange = async item => {
		this.fulfillmentOrders = [];
		this.palletOptions = [];
		this.selectedPallet = null;
		this.exitScanNotification = null;
		this.selectedWarehouse = item;
		if (item != null) {
			this.form.$('warehouse').value = item.value;
			await this.findPallet();
		} else {
			this.form.$('warehouse').value = '';
			this.selectReference.select.focus();
		}
	};

	@action.bound
	setReference = reference => {
		if (reference != null) {
			this.selectReference = reference;
		}
	};

	@action.bound
	defineTableData = () => {
		this.tableData = {
			columns: [
				{
					key: 'fulfillmentOrderId',
					title: 'FULFILLMENTORDER.EXITSCANMODE.TABLEDATA.FULFILLMENTORDERID',
					dataCell: HyperlinkCellData,
					onClick: dataItem => this.openDetailPage(dataItem.id),
					selected: true,
					width: 85,
					filter: null,
				},
				{
					key: 'fulfillmentOrderStatus.name',
					title: 'FULFILLMENTORDER.EXITSCANMODE.TABLEDATA.STATUS',
					selected: true,
					width: 120,
					filter: null,
				},
				{
					key: 'product.sku',
					title: 'FULFILLMENTORDER.EXITSCANMODE.TABLEDATA.PRODUCTSKU',
					selected: true,
					width: 150,
					filter: null,
				},
				{
					key: 'productImage',
					title: 'FULFILLMENTORDER.EXITSCANMODE.TABLEDATA.IMAGE',
					dataCell: ImageCellData,
					selected: true,
					width: 175,
					filter: null,
				},
			],
			actions: {},
			sortObject: [],
		};
	};

	@action.bound
	transformData = data => {
		let product = {};
		let fulfillmentOrder = {};
		_.forEach(data.shipmentInfos, item => {
			if (item.fulfillmentOrder.fulfillmentOrderItems.length != 1) {
				product.name = 'Multiple';
				product.sku = 'Multiple';
			} else {
				product.name = item.fulfillmentOrder.fulfillmentOrderItems[0].product.name;
				product.sku = item.fulfillmentOrder.fulfillmentOrderItems[0].product.mainSku;
			}
			fulfillmentOrder = item.fulfillmentOrder;
			fulfillmentOrder.fulfillmentOrderStatus.name = 'Exit Scan';
			fulfillmentOrder.product = product;
			this.fulfillmentOrders.push(fulfillmentOrder);
			product = {};
			fulfillmentOrder = {};
		});
	};

	onDetails = () => {
		this.rootStore.routerStore.goTo('master.app.main.order.exitscanmode.detail', {
			id: this.selectedPallet.extra.id,
		});
	};

	openDetailPage = id => {
		this.rootStore.routerStore.goTo('master.app.main.order.list.detail', {
			id: id,
		});
	};
}

export default ExitScanModeViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/ExitScanModeViewStore.js